import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import WorldImpactMap from "@/components/WorldImpactMap";
import { trackEvent } from "@/lib/analytics";

const revealLine = (text, delay = 0, className = "") => (
    <span className="mask-reveal">
        <motion.span
            className={`inline-block ${className}`}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: [0.76, 0, 0.24, 1] }}
        >
            {text}
        </motion.span>
    </span>
);

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100svh] flex flex-col justify-between pt-28 md:pt-32 pb-8 md:pb-10 overflow-hidden"
        >
            {/* Ambient background glow */}
            <div aria-hidden className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-40 -right-40 w-[80vw] h-[80vw] rounded-full opacity-30"
                    style={{ background: "radial-gradient(circle, rgba(201,162,75,0.20) 0%, rgba(11,30,63,0) 60%)" }} />
                <div className="absolute -bottom-40 -left-40 w-[60vw] h-[60vw] rounded-full opacity-20"
                    style={{ background: "radial-gradient(circle, rgba(46,139,87,0.30) 0%, rgba(11,30,63,0) 60%)" }} />
            </div>

            <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 w-full">
                <div className="grid md:grid-cols-12 gap-6 items-end mb-6 md:mb-8">
                    <div className="md:col-span-7">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.15, duration: 0.8 }}
                            className="label-mono mb-5 flex items-center gap-3"
                            data-testid="hero-eyebrow"
                        >
                            <span className="w-8 h-px bg-[var(--gold)]" />
                            Z ED SCHOOL OF GLOBAL IMPACT · A LIVING MAP
                        </motion.div>

                        <h1 data-testid="hero-headline" className="serif-display text-[clamp(2.6rem,7.5vw,6.5rem)] leading-[0.92]">
                            {revealLine("A movement,", 0.35, "text-[var(--ivory)] mr-3")}{" "}
                            {revealLine("mapped.", 0.48, "text-[var(--gold)] serif-italic")}
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.1, duration: 0.7 }}
                            className="mt-6 max-w-xl text-base md:text-lg text-[var(--ivory)]/80 font-light"
                            data-testid="hero-subheading"
                        >
                            Tap a node. Meet the work happening in that region — then join the chapter that speaks to you. Policy. People. Planet. Peace. Progress.
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.7 }}
                        className="md:col-span-5 flex md:justify-end items-end gap-3"
                    >
                        <Link
                            to="/apply"
                            onClick={() => trackEvent("CPS_Apply_Click", { source: "hero_cta" })}
                            className="btn-gold"
                            data-testid="hero-cta-apply"
                        >
                            Apply for 2026 <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link to="/programs/cps-2026" className="btn-ghost hidden sm:inline-flex" data-testid="hero-cta-explore">
                            Explore CPS 2026
                        </Link>
                    </motion.div>
                </div>

                {/* THE MAP — primary focus */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 1, ease: [0.76, 0, 0.24, 1] }}
                    className="relative border border-[rgba(201,162,75,0.35)] bg-[rgba(6,17,36,0.55)] backdrop-blur-sm p-2 md:p-4"
                >
                    <div className="absolute top-2 left-2 md:top-4 md:left-4 label-mono text-[var(--gold)] z-10 pointer-events-none">
                        — GLOBAL REACH · 2026
                    </div>
                    <WorldImpactMap />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6, duration: 0.8 }}
                    className="mt-5 md:mt-6 flex items-center justify-between flex-wrap gap-3"
                >
                    <div className="label-mono text-[var(--ivory)]/60">
                        Learn · Lead · Empower — <span className="text-[var(--gold)]">Youth-led. Values-driven. Impact-focused.</span>
                    </div>
                    <div className="label-mono flex items-center gap-2 text-[var(--ivory)]/50">
                        <ArrowDown className="w-3 h-3" /> Scroll to explore the manifesto
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
